import { Button, Modal } from "@instacover/ui"
import { ArrowRightIcon, IconUndo } from "@instacover/ui/icons"
import { isNil } from "@instacover/utils"
import { createId as cuid } from "@paralleldrive/cuid2"
// TODO placeholder icons — replace with the icons provided by design
import { IconReload } from "@tabler/icons-react"
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { BaseLayout } from "@/components/layouts/BaseLayout"
import { SpinnerLayout } from "@/components/layouts/SpinnerLayout"
import { LOCAL_STORAGE_KEYS } from "@/constants/localStorage"
import { LINKS } from "@/routing/links"
import { ImageHighlighter } from "@/sections/claim/imageHighlighter/ImageHighlighter"
import { LineObject } from "@/sections/claim/imageHighlighter/ImageHighlighter.types"
import { addClaimMarker, useClaimMarkersByPhoto, usePhotoById } from "@/services/dexie"
import { useLocalStorage } from "@/utils/hooks/useLocalStorage"
import { usePhotoSrc } from "@/utils/hooks/usePhotoSrc"

export const Route = createFileRoute("/$sessionId/_session/claim/photos/$photoId/new-marker")({
  component: ClaimNewMarkerPage,
})

function ClaimNewMarkerPage() {
  const { t } = useTranslation()
  const { sessionId, photoId } = Route.useParams()
  const navigate = useNavigate()

  const { data: photo } = usePhotoById(photoId)
  const imageUrl = usePhotoSrc({ photo, variant: "full" })
  const { data: markers } = useClaimMarkersByPhoto(photoId)

  const [lines, setLines] = useState<LineObject[]>([])
  const [saving, setSaving] = useState(false)

  // opens on the first visit within a session
  const [guideSeen, setGuideSeen] = useLocalStorage(`${LOCAL_STORAGE_KEYS.claimGuideSeen}:${sessionId}`, false)
  const [isGuideModalOpen, setGuideModalOpen] = useState(!guideSeen)

  if (!photo) {
    return <SpinnerLayout />
  }

  // Existing markers of this photo are shown as read-only reference lines
  const extraLines = (markers ?? []).map((marker) => ({ points: marker.points, width: marker.width }))

  async function handleContinue() {
    const line = lines.at(0)
    if (isNil(line) || saving) return

    setSaving(true)
    const markerId = cuid()
    await addClaimMarker({ id: markerId, sessionId, photoId, points: line.points, width: line.width })
    await navigate({ to: LINKS.claimMarkerDetails(sessionId, markerId) })
  }

  return (
    <BaseLayout dataTestId="claim-new-marker">
      <div className="h-vh-header flex flex-col px-6 pb-6">
        <ImageHighlighter
          imageUrl={imageUrl}
          className="bg-ic-background-muted min-h-0 w-full min-w-0 flex-1"
          lines={lines}
          setLines={setLines}
          maxLines={1}
          extraLines={extraLines}
          dataTestId="claim-marker-canvas"
        />

        <div className="mt-4 flex h-6 shrink-0 items-center justify-end">
          <PhotoActions
            sessionId={sessionId}
            photoId={photoId}
            hasLine={lines.length > 0}
            hasSavedMarkers={(markers ?? []).length > 0}
            onClear={() => setLines([])}
          />
        </div>

        <div className="mt-6 flex shrink-0 flex-col gap-3">
          <Button
            variant="secondary"
            className="w-full"
            onClick={() => setGuideModalOpen(true)}
            data-testid="claim-marker-how-btn"
          >
            {t("claim.markDamage.how")}
          </Button>

          <Button
            className="w-full"
            disabled={lines.length === 0 || saving}
            loading={saving}
            onClick={handleContinue}
            data-testid="claim-marker-continue-btn"
          >
            {t("claim.markDamage.button")}
          </Button>
        </div>

        <GuideModal
          isOpen={isGuideModalOpen}
          onOpenChange={(open) => {
            setGuideModalOpen(open)
            if (!open) setGuideSeen(true)
          }}
        />
      </div>
    </BaseLayout>
  )
}

type GuideModalProps = {
  isOpen: boolean
  onOpenChange(open: boolean): void
}

function GuideModal({ isOpen, onOpenChange }: GuideModalProps) {
  const { t } = useTranslation()

  return (
    <Modal.Root open={isOpen} onOpenChange={onOpenChange}>
      <Modal.Content className="w-[calc(100%-3rem)] max-w-none" data-testid="claim-marker-guide-modal">
        <h6 className="text-ic-primary text-base font-bold uppercase">{t("claim.markDamage.subheading")}</h6>

        <Modal.Title render={<h1 className="text-ic-heading mt-2 text-2xl font-bold" />}>
          {t("claim.markDamage.heading")}
        </Modal.Title>

        <Modal.Description render={<p className="text-ic-text mt-5 text-base" />}>
          {t("claim.markDamage.description")}
        </Modal.Description>

        <Modal.Close render={<Button className="mt-6 w-full" />} data-testid="claim-marker-guide-close-btn">
          {t("claim.markDamage.buttonModal")}
        </Modal.Close>
      </Modal.Content>
    </Modal.Root>
  )
}

type PhotoActionsProps = {
  sessionId: string
  photoId: string
  hasLine: boolean
  hasSavedMarkers: boolean
  onClear: () => void
}

function PhotoActions({ sessionId, photoId, hasLine, hasSavedMarkers, onClear }: PhotoActionsProps) {
  const { t } = useTranslation()

  // Marker is being drawn — offer to clear it
  if (hasLine) {
    return (
      <button type="button" className="text-ic-error flex items-center gap-2 text-base font-bold" onClick={onClear}>
        {t("claim.markDamage.clear")}
        <IconUndo className="size-5" />
      </button>
    )
  }

  // Return back to /photos/select
  if (hasSavedMarkers && !hasLine) {
    return (
      <Link
        to={LINKS.claimPhotosSelect(sessionId)}
        className="text-ic-primary flex items-center gap-2 text-base font-bold"
      >
        <ArrowRightIcon className="size-5 rotate-180" />
        {t("claim.markDamage.backToSelect")}
      </Link>
    )
  }

  // No marker yet — offer to retake the photo
  return (
    <Link
      to="/$sessionId/claim/new"
      params={{ sessionId }}
      search={{ replacesPhotoId: photoId }}
      className="text-ic-primary flex items-center gap-2 text-base font-bold"
    >
      {t("claim.markDamage.retake")}
      {/* TODO placeholder icon */}
      <IconReload className="size-5" />
    </Link>
  )
}
